import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../config/apiConfig';
import axios from 'axios';

const api = axios.create({ baseURL: API_URL });

const EmailHealthScreen = () => {
  const { isAdmin, token } = useAuth();
  const [tab, setTab] = useState('suppressions');
  const [suppressions, setSuppressions] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventPagination, setEventPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eventFilter, setEventFilter] = useState('');
  const [emailSearch, setEmailSearch] = useState('');

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  const fetchSuppressions = useCallback(async () => {
    try {
      const res = await api.get('/email/suppressions');
      setSuppressions(res.data.suppressions || []);
    } catch (err) {
      console.error('Failed to fetch suppressions:', err);
    }
  }, []);

  const fetchEvents = useCallback(async (page = 1) => {
    try {
      const params = { page, pageSize: 50 };
      if (eventFilter) params.event_type = eventFilter;
      if (emailSearch) params.email = emailSearch;
      const res = await api.get('/email/events', { params });
      setEvents(res.data.events || []);
      setEventPagination(res.data.pagination || { page: 1, total: 0, totalPages: 0 });
    } catch (err) {
      console.error('Failed to fetch events:', err);
    }
  }, [eventFilter, emailSearch]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/email/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch email stats:', err);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchSuppressions(), fetchStats(), fetchEvents()]).finally(() => setLoading(false));
  }, [fetchSuppressions, fetchStats, fetchEvents]);

  const handleUnsuppress = async (id) => {
    if (!window.confirm('Re-enable sending to this address? Only do this if you are sure the address is valid.')) return;
    try {
      await api.patch(`/email/suppressions/${id}`, { is_active: false });
      fetchSuppressions();
    } catch (err) {
      alert('Failed to un-suppress');
    }
  };

  const handleDeleteSuppression = async (id) => {
    if (!window.confirm('Permanently remove this suppression record?')) return;
    try {
      await api.delete(`/email/suppressions/${id}`);
      fetchSuppressions();
    } catch (err) {
      alert('Failed to delete');
    }
  };

  const handleSimulate = async (eventType) => {
    const email = prompt(`Enter email address to simulate a ${eventType} event:`);
    if (!email) return;
    try {
      await api.post('/email/ses-notifications/simulate', {
        email,
        event_type: eventType,
        ...(eventType === 'Bounce' ? { bounce_type: 'Permanent', bounce_subtype: 'General' } : {}),
        ...(eventType === 'Complaint' ? { complaint_type: 'abuse' } : {})
      });
      alert(`Simulated ${eventType} for ${email}`);
      fetchSuppressions();
      fetchEvents();
      fetchStats();
    } catch (err) {
      alert('Simulation failed: ' + (err.response?.data?.error || err.message));
    }
  };

  if (!isAdmin) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Admin access required</div>;
  }

  const tabStyle = (active) => ({
    padding: '8px 20px',
    border: 'none',
    borderBottom: active ? '3px solid #2563eb' : '3px solid transparent',
    background: 'none',
    cursor: 'pointer',
    fontWeight: active ? '600' : '400',
    color: active ? '#2563eb' : '#666',
    fontSize: '0.9rem'
  });

  const badgeStyle = (color) => ({
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '0.75rem',
    fontWeight: '600',
    backgroundColor: color + '18',
    color: color,
    border: `1px solid ${color}40`
  });

  const eventTypeColor = (type) => {
    switch (type) {
      case 'Bounce': return '#dc3545';
      case 'Complaint': return '#e53e3e';
      case 'Reject': return '#f59e0b';
      case 'DeliveryDelay': return '#f59e0b';
      case 'RenderingFailure': return '#dc3545';
      case 'Delivery': return '#16a34a';
      case 'Send': return '#2563eb';
      case 'SendFailure': return '#dc3545';
      default: return '#666';
    }
  };

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '1.4rem', fontWeight: '700', color: '#333', margin: '0 0 4px 0' }}>
          Email Health
        </h1>
        <p style={{ color: '#888', fontSize: '0.82rem', margin: 0 }}>
          Monitor SES events, bounces, complaints, and suppressed addresses
        </p>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginBottom: '24px' }}>
          {(stats.eventCounts || []).map(ec => (
            <div key={ec.event_type} style={{
              background: 'white',
              borderRadius: '10px',
              padding: '16px',
              border: `1px solid ${eventTypeColor(ec.event_type)}30`,
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: eventTypeColor(ec.event_type) }}>
                {ec.count}
              </div>
              <div style={{ fontSize: '0.78rem', color: '#666', marginTop: '4px' }}>
                {ec.event_type}
              </div>
            </div>
          ))}
          <div style={{
            background: 'white',
            borderRadius: '10px',
            padding: '16px',
            border: '1px solid #dc354530',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#dc3545' }}>
              {suppressions.filter(s => s.is_active).length}
            </div>
            <div style={{ fontSize: '0.78rem', color: '#666', marginTop: '4px' }}>
              Suppressed
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid #e9ecef', marginBottom: '20px', display: 'flex', gap: '4px' }}>
        <button style={tabStyle(tab === 'suppressions')} onClick={() => setTab('suppressions')}>
          Suppressions ({suppressions.filter(s => s.is_active).length})
        </button>
        <button style={tabStyle(tab === 'events')} onClick={() => setTab('events')}>
          Events
        </button>
        <button style={tabStyle(tab === 'simulate')} onClick={() => setTab('simulate')}>
          Test / Simulate
        </button>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Loading...</div>}

      {/* Suppressions Tab */}
      {!loading && tab === 'suppressions' && (
        <div style={{ background: 'white', borderRadius: '10px', padding: '20px', border: '1px solid #e9ecef' }}>
          {suppressions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
              <div style={{ fontSize: '2rem', marginBottom: '8px' }}>✅</div>
              No suppressed email addresses
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                  <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '0.8rem', color: '#666' }}>Email</th>
                  <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '0.8rem', color: '#666' }}>Reason</th>
                  <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '0.8rem', color: '#666' }}>Details</th>
                  <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '0.8rem', color: '#666' }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '0.8rem', color: '#666' }}>Status</th>
                  <th style={{ textAlign: 'center', padding: '10px 8px', fontSize: '0.8rem', color: '#666' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {suppressions.map(s => (
                  <tr key={s.suppression_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '10px 8px', fontSize: '0.85rem', fontWeight: '500' }}>{s.email}</td>
                    <td style={{ padding: '10px 8px' }}>
                      <span style={badgeStyle(s.reason === 'complaint' ? '#e53e3e' : s.reason === 'hard_bounce' ? '#dc3545' : '#666')}>
                        {s.reason}
                      </span>
                    </td>
                    <td style={{ padding: '10px 8px', fontSize: '0.82rem', color: '#666' }}>{s.details}</td>
                    <td style={{ padding: '10px 8px', fontSize: '0.82rem', color: '#888' }}>
                      {new Date(s.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td style={{ padding: '10px 8px' }}>
                      <span style={badgeStyle(s.is_active ? '#dc3545' : '#16a34a')}>
                        {s.is_active ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                      {s.is_active && (
                        <button
                          onClick={() => handleUnsuppress(s.suppression_id)}
                          style={{
                            background: '#16a34a',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 10px',
                            cursor: 'pointer',
                            fontSize: '0.78rem',
                            marginRight: '6px'
                          }}
                        >
                          Un-suppress
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteSuppression(s.suppression_id)}
                        style={{
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '4px 10px',
                          cursor: 'pointer',
                          fontSize: '0.78rem'
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Events Tab */}
      {!loading && tab === 'events' && (
        <div>
          {/* Filters */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <select
              value={eventFilter}
              onChange={(e) => { setEventFilter(e.target.value); fetchEvents(1); }}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }}
            >
              <option value="">All Event Types</option>
              <option value="Send">Send</option>
              <option value="Delivery">Delivery</option>
              <option value="Bounce">Bounce</option>
              <option value="Complaint">Complaint</option>
              <option value="Reject">Reject</option>
              <option value="DeliveryDelay">DeliveryDelay</option>
              <option value="RenderingFailure">RenderingFailure</option>
            </select>
            <input
              type="text"
              value={emailSearch}
              onChange={(e) => setEmailSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchEvents(1); }}
              placeholder="Search by email..."
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', minWidth: '200px' }}
            />
            <button
              onClick={() => fetchEvents(1)}
              style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#2563eb', color: 'white', cursor: 'pointer', fontSize: '13px' }}
            >
              Search
            </button>
          </div>

          <div style={{ background: 'white', borderRadius: '10px', padding: '20px', border: '1px solid #e9ecef' }}>
            {events.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📭</div>
                No email events recorded yet
              </div>
            ) : (
              <>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                      <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '0.8rem', color: '#666' }}>Date</th>
                      <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '0.8rem', color: '#666' }}>Type</th>
                      <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '0.8rem', color: '#666' }}>Email</th>
                      <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '0.8rem', color: '#666' }}>Details</th>
                      <th style={{ textAlign: 'left', padding: '10px 8px', fontSize: '0.8rem', color: '#666' }}>Message ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map(ev => (
                      <tr key={ev.event_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '10px 8px', fontSize: '0.82rem', color: '#888', whiteSpace: 'nowrap' }}>
                          {new Date(ev.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <span style={badgeStyle(eventTypeColor(ev.event_type))}>
                            {ev.event_type}
                          </span>
                        </td>
                        <td style={{ padding: '10px 8px', fontSize: '0.85rem' }}>{ev.email}</td>
                        <td style={{ padding: '10px 8px', fontSize: '0.82rem', color: '#666' }}>
                          {ev.bounce_type && `${ev.bounce_type}/${ev.bounce_subtype}`}
                          {ev.complaint_type && `Complaint: ${ev.complaint_type}`}
                          {!ev.bounce_type && !ev.complaint_type && '—'}
                        </td>
                        <td style={{ padding: '10px 8px', fontSize: '0.75rem', color: '#aaa', fontFamily: 'monospace', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ev.message_id || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {eventPagination.totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #e9ecef' }}>
                    <button
                      disabled={eventPagination.page <= 1}
                      onClick={() => fetchEvents(eventPagination.page - 1)}
                      style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #ddd', background: 'white', cursor: eventPagination.page <= 1 ? 'not-allowed' : 'pointer', color: eventPagination.page <= 1 ? '#ccc' : '#333', fontSize: '0.82rem' }}
                    >
                      ← Prev
                    </button>
                    <span style={{ padding: '6px 12px', fontSize: '0.82rem', color: '#666' }}>
                      Page {eventPagination.page} of {eventPagination.totalPages} ({eventPagination.total} events)
                    </span>
                    <button
                      disabled={eventPagination.page >= eventPagination.totalPages}
                      onClick={() => fetchEvents(eventPagination.page + 1)}
                      style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #ddd', background: 'white', cursor: eventPagination.page >= eventPagination.totalPages ? 'not-allowed' : 'pointer', color: eventPagination.page >= eventPagination.totalPages ? '#ccc' : '#333', fontSize: '0.82rem' }}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Simulate Tab */}
      {!loading && tab === 'simulate' && (
        <div style={{ background: 'white', borderRadius: '10px', padding: '24px', border: '1px solid #e9ecef' }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem', color: '#333' }}>Simulate SES Events</h3>
          <p style={{ color: '#888', fontSize: '0.82rem', margin: '0 0 20px 0' }}>
            Test the bounce/complaint handling by simulating SES events. This inserts events into the database
            and triggers suppression logic, just like a real SNS notification would.
          </p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={() => handleSimulate('Bounce')}
              style={{
                padding: '10px 20px', borderRadius: '8px', border: 'none',
                background: '#dc3545', color: 'white', cursor: 'pointer',
                fontSize: '0.85rem', fontWeight: '600'
              }}
            >
              Simulate Hard Bounce
            </button>
            <button
              onClick={() => handleSimulate('Complaint')}
              style={{
                padding: '10px 20px', borderRadius: '8px', border: 'none',
                background: '#e53e3e', color: 'white', cursor: 'pointer',
                fontSize: '0.85rem', fontWeight: '600'
              }}
            >
              Simulate Complaint
            </button>
            <button
              onClick={() => handleSimulate('Delivery')}
              style={{
                padding: '10px 20px', borderRadius: '8px', border: 'none',
                background: '#16a34a', color: 'white', cursor: 'pointer',
                fontSize: '0.85rem', fontWeight: '600'
              }}
            >
              Simulate Delivery
            </button>
            <button
              onClick={() => handleSimulate('Send')}
              style={{
                padding: '10px 20px', borderRadius: '8px', border: 'none',
                background: '#2563eb', color: 'white', cursor: 'pointer',
                fontSize: '0.85rem', fontWeight: '600'
              }}
            >
              Simulate Send
            </button>
          </div>

          {/* Recent bounces & complaints from stats */}
          {stats?.recentBounces?.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <h4 style={{ fontSize: '0.9rem', color: '#333', margin: '0 0 8px 0' }}>Recent Bounces (7 days)</h4>
              <div style={{ fontSize: '0.82rem', color: '#666' }}>
                {stats.recentBounces.map((b, i) => (
                  <div key={i} style={{ padding: '4px 0' }}>
                    <span style={{ fontWeight: '500' }}>{b.email}</span>
                    {' — '}
                    <span style={badgeStyle('#dc3545')}>{b.bounce_type}/{b.bounce_subtype}</span>
                    {' '}
                    <span style={{ color: '#aaa' }}>{new Date(b.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stats?.recentComplaints?.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <h4 style={{ fontSize: '0.9rem', color: '#333', margin: '0 0 8px 0' }}>Recent Complaints (30 days)</h4>
              <div style={{ fontSize: '0.82rem', color: '#666' }}>
                {stats.recentComplaints.map((c, i) => (
                  <div key={i} style={{ padding: '4px 0' }}>
                    <span style={{ fontWeight: '500' }}>{c.email}</span>
                    {' — '}
                    <span style={badgeStyle('#e53e3e')}>{c.complaint_type || 'abuse'}</span>
                    {' '}
                    <span style={{ color: '#aaa' }}>{new Date(c.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailHealthScreen;
